import { useMemo } from 'react';
import type { MangoQuery } from 'rxdb';

/**
 * Hold a Mango query steady across renders.
 *
 * RxDB's `useLiveRxQuery` re-runs whenever the query OBJECT changes identity,
 * and a query written inline is a new object every render: subscribe, results,
 * re-render, subscribe again — a loop that never settles. The old rxdb-hooks
 * never showed this because it handed the query to RxDB's query cache, which
 * recognised it by value.
 *
 * So recognise it by value here. A Mango query is JSON by definition, and a
 * call site writes its keys in the same order every time, which makes the
 * serialized form an honest identity — and a safer one than a dependency list
 * at two hundred call sites, where a forgotten entry means stale data on
 * screen.
 */
export function useStableQuery<T>(query: MangoQuery<T>): MangoQuery<T> {
    const key = JSON.stringify(query);
    // The key IS the query, in every respect that matters to RxDB. Listing
    // `query` beside it would defeat the purpose: that object is new every
    // render, which is the very thing being held still here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => query, [key]);
}
